// Copyright (c) 2025, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AppConfig } from "../../config/config";
import { enqueueSnackbarMessage } from "../commonSlice/common";
import { APIService } from "../../utils/apiService";
import { ExamExtensionErrorMessages } from "../../config/constant";

interface ExtendExamPayload {
  examRecordId: string; 
  contactId: string;
  examId?: string;
}

interface ExtendExamResponse {
  success: boolean;
  message: string;
  newExpirationDate?: string;
}

interface ExtendExamState {
  state: "failed" | "success" | "loading" | "idle";
  stateMessage: string | null;
  errorMessage: string | null;
  response: ExtendExamResponse | null;
  backgroundProcess: boolean;
  backgroundProcessMessage: string | null;
}

const initialState: ExtendExamState = {
  state: "idle",
  stateMessage: null,
  errorMessage: null,
  response: null,
  backgroundProcess: false,
  backgroundProcessMessage: null,
};

function getExamExtensionErrorMessage(error: any): string {
  const errorCode = error?.response?.data?.errorCode;
  return ExamExtensionErrorMessages[errorCode] || error?.response?.data?.message || "An unexpected error occurred.";
}

export const extendExam = createAsyncThunk(
  "exams/extendExam",
  async (payload: ExtendExamPayload, { dispatch }) => {
    try {
      const resp = await APIService.getInstance().post(
        `${AppConfig.serviceUrls.getExam}/${payload.examRecordId}/extend`,
        {
          contactId: payload.contactId,
          examId: payload.examId, 
        }
      );
      
      const isSuccessStatus = resp.status === 201 || (resp.status >= 200 && resp.status < 300 && resp.data?.success === true);
      if (isSuccessStatus) {
        const response: ExtendExamResponse = {
          success: true,
          message: resp.data.message || "Exam extended successfully.",
          newExpirationDate: resp.data.newExpirationDate,
        };
        dispatch(
          enqueueSnackbarMessage({
            message: response.message,
            type: "success",
          })
        );
        return response;
      } else {
        let errorMsg = resp.data?.message || "Failed to extend exam.";
        const errorCode = resp.data?.errorCode;
        if (errorCode && ExamExtensionErrorMessages[errorCode]) {
          errorMsg = ExamExtensionErrorMessages[errorCode];
        }
        const customError = new Error(errorMsg);
        (customError as any).isCustomError = true;
        throw customError;
      }
    } catch (error: any) {
      let errorMessage: string;
      
      if ((error as any).isCustomError) {
        errorMessage = error.message;
      } else {
        errorMessage = getExamExtensionErrorMessage(error);
      }
      
      dispatch(
        enqueueSnackbarMessage({
          message: errorMessage,
          type: "error",
        })
      );
      throw new Error(errorMessage);
    }
  }
);

export const extendExamSlice = createSlice({
  name: "extendExam",
  initialState,
  reducers: {
    resetExtendExamState: (state) => {
      state.state = "idle";
      state.stateMessage = null;
      state.errorMessage = null;
      state.response = null;
      state.backgroundProcess = false;
      state.backgroundProcessMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(extendExam.pending, (state) => {
        state.stateMessage = "Extending Exam";
        state.state = "loading";
        state.backgroundProcess = true;
        state.backgroundProcessMessage = "Processing exam extension request...";
      })
      .addCase(extendExam.fulfilled, (state, action) => {
        state.response = action.payload;
        state.state = "success";
        state.backgroundProcess = false;
        state.backgroundProcessMessage = null;
      })
      .addCase(extendExam.rejected, (state, action) => {
        state.state = "failed";
        state.backgroundProcess = false;
        state.backgroundProcessMessage = null;
        state.errorMessage = action.error.message || "Failed to extend exam";
      });
  },
});

export const { resetExtendExamState } = extendExamSlice.actions;
export default extendExamSlice.reducer;
